"use client";

import {
  Phone,
  Mail,
  MapPin,
  Send,
  User,
  Briefcase,
  Clock,
  Building2,
  IndianRupee,
  Calendar,
  Upload,
} from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { motion } from "motion/react";
import { Button } from "../../components/ui/button";
import { useAppDispatch, useAppSelector } from "../../_hooks/hooks";
import { ICMS } from "../home/homeModel";
import { usePathname, useRouter } from "next/navigation";
import dynamic from "next/dynamic";

const Preloader = dynamic(() =>
  import("../../components/Preloader").then((mod) => mod.Preloader),
);

import { useEffect, useState } from "react";
import { createLookupObject } from "../../_helper/helper";
import {
  getAllActivePosition,
  insertJobApplication,
} from "./services/career.services";
import { ErrorToast } from "@/components/common/Toast";

export default function Career({ cmsData }: { cmsData?: any[] }) {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const path = pathname?.split("/")?.[1];
  const applicationLoading = useAppSelector(
    (state) => state.career.applicationLoading,
  );
  const formLoading = useAppSelector((state) => state.home.formLoading);
  const BannerList = useAppSelector((state) => state.home.BannerList);
  const PropertyList = useAppSelector((state) => state.property.PropertyList);
  const PositionList = useAppSelector((state) => state.career.PositionList);

  const [lookUpCMSData, setLookUpCMSData] = useState<Record<string, ICMS>>({});

  const banner: any =
    BannerList?.find((banner: any) => banner?.BannerType === path) || {};

  useEffect(() => {
    if (cmsData && cmsData?.length > 0) {
      setLookUpCMSData(createLookupObject(cmsData, "Name"));
    }
  }, [cmsData]);

  useEffect(() => {
    dispatch(getAllActivePosition());
  }, []);

  const formik = useFormik({
    initialValues: {
      JobTitle: "",
      FullName: "",
      EmailID: "",
      MobileNo: "",
      PropertyID: 0,
      PositionID: 0,
      TotalExperience: "",
      CurrentCompany: "",
      CurrentSalary: "",
      ExpectedSalary: "",
      NoticePeriod: "",
      UploadResume: null as File | null,
    },
    validationSchema: Yup.object({
      JobTitle: Yup.string().required("Job Title is required"),
      FullName: Yup.string().required("Full Name is required"),
      EmailID: Yup.string()
        .email("Invalid email")
        .required("Email is required"),
      MobileNo: Yup.string()
        .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
        .required("Mobile number is required"),
      PropertyID: Yup.number()
        .moreThan(0, "Please select a property")
        .required("Property is required"),
      PositionID: Yup.number()
        .moreThan(0, "Please select a position")
        .required("Position is required"),
      TotalExperience: Yup.string().required("Total Experience is required"),
      CurrentCompany: Yup.string(),
      CurrentSalary: Yup.number().when("CurrentCompany", {
        is: (val: string) => val && val.length > 0,
        then: (schema) => schema.required("Current Salary is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      ExpectedSalary: Yup.number().required("Expected Salary is required"),
      NoticePeriod: Yup.string().when("CurrentCompany", {
        is: (val: string) => val && val.length > 0,
        then: (schema) => schema.required("Notice Period is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      UploadResume: Yup.mixed().required("Resume is required"),
    }),
    onSubmit: async (
      values,
      {
        // setSubmitting,
        resetForm,
      },
    ) => {
      // Prepared FormData payload for binary data transmission
      const formData = new FormData();
      formData.append("JobTitle", values?.JobTitle);
      formData.append("FullName", values?.FullName);
      formData.append("EmailID", values?.EmailID);
      formData.append("MobileNo", values?.MobileNo);
      formData.append("PropertyID", String(values?.PropertyID));
      formData.append("PositionID", String(values?.PositionID));
      formData.append("TotalExperience", values?.TotalExperience);
      formData.append("CurrentCompany", values?.CurrentCompany || "");
      formData.append("CurrentSalary", String(values?.CurrentSalary || ""));
      formData.append("ExpectedSalary", String(values?.ExpectedSalary));
      formData.append("NoticePeriod", values?.NoticePeriod || "");

      if (values?.UploadResume) {
        formData.append("UploadResume", values?.UploadResume);
      }
      // await dispatch(insertInquiry(formData));
      try {
        const response = await dispatch(insertJobApplication(formData));
        if (response.meta.requestStatus === "fulfilled") {
          resetForm();
          router.push("/thankyou/");
        } else {
          ErrorToast("Error sending message!");
        }
      } catch (error) {
        // console.log("error", error);
      }
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (file) {
      formik.setFieldValue("UploadResume", file);
    }
  };

  return (
    <>
      {applicationLoading && <Preloader />}
      <div className="min-h-screen bg-white text-md">
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-end justify-center overflow-hidden">
          <div
            className="absolute inset-0 z-0"
            style={{
              backgroundImage: `url(${banner?.BannerImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 mt-25 mb-15">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {banner?.SubTitle && (
                <div className="inline-block px-6 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-8">
                  <p className="text-[#C9A962] tracking-[0.4em] text-sm uppercase">
                    {banner?.SubTitle || "Join Our Team"}
                  </p>
                </div>
              )}

              <h1 className="text-white text-3xl md:text-4xl lg:text-5xl mb-8 leading-tight font-playfair">
                <span className="text-[#C9A962]">
                  {banner?.BannerTitle || "Build Your Career With Us"}
                </span>
              </h1>

              {banner?.Description && (
                <p
                  className="text-white/95 text-xl md:text-2xl max-w-3xl mx-auto font-playfair"
                  dangerouslySetInnerHTML={{
                    __html: banner?.Description,
                  }}
                />
              )}
            </motion.div>
          </div>
        </section>

        {/* Application Form Section */}
        <section className="py-10 md:py-20 bg-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#C9A962]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#B42025]/5 rounded-full blur-3xl" />

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-5xl mx-auto">
              {lookUpCMSData?.["Career - Section 1 : Career Opportunities"] ? (
                <div
                  className="text-center mb-16"
                  dangerouslySetInnerHTML={{
                    __html:
                      lookUpCMSData?.[
                        "Career - Section 1 : Career Opportunities"
                      ]?.Template,
                  }}
                />
              ) : (
                <div className="text-center mb-16">
                  <p className="text-[#B42025] tracking-[0.3em] mb-4 text-sm uppercase">
                    Apply Now
                  </p>
                  <h2 className="text-5xl md:text-6xl mb-6 text-[#1A1A1A]">
                    Career Opportunities
                  </h2>
                  <div className="w-24 h-1 bg-[#C9A962] mx-auto mb-8" />
                  <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                    Take the first step towards a rewarding career at Kavish
                    Hotels & Resorts.
                  </p>
                </div>
              )}

              <div className="bg-[#F9F7F4] rounded-3xl p-8 md:p-12 shadow-2xl">
                <form
                  onSubmit={formik.handleSubmit}
                  className="grid grid-cols-1 md:grid-cols-2 gap-8"
                >
                  {/* Job Title */}
                  <div>
                    <label className="flex items-center gap-2 text-gray-700 mb-3">
                      <Briefcase className="h-4 w-4 text-[#B42025]" />
                      <span>Job Title *</span>
                    </label>
                    <input
                      name="JobTitle"
                      type="text"
                      placeholder="Enter Job Title"
                      className={`w-full px-6 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#B42025] transition-colors bg-white ${
                        formik.errors.JobTitle && formik.touched.JobTitle
                          ? "border-red-500"
                          : ""
                      }`}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.JobTitle}
                    />
                  </div>

                  {/* Full Name */}
                  <div>
                    <label className="flex items-center gap-2 text-gray-700 mb-3">
                      <User className="h-4 w-4 text-[#B42025]" />
                      <span>Full Name *</span>
                    </label>
                    <input
                      name="FullName"
                      type="text"
                      placeholder="Enter Your Full Name"
                      className={`w-full px-6 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#B42025] transition-colors bg-white ${
                        formik.errors.FullName && formik.touched.FullName
                          ? "border-red-500"
                          : ""
                      }`}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.FullName}
                    />
                  </div>

                  {/* Email ID */}
                  <div>
                    <label className="flex items-center gap-2 text-gray-700 mb-3">
                      <Mail className="h-4 w-4 text-[#B42025]" />
                      <span>Email Address *</span>
                    </label>
                    <input
                      name="EmailID"
                      type="email"
                      placeholder="example@gmail.com"
                      className={`w-full px-6 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#B42025] transition-colors bg-white ${
                        formik.errors.EmailID && formik.touched.EmailID
                          ? "border-red-500"
                          : ""
                      }`}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.EmailID}
                    />
                  </div>

                  {/* Mobile No */}
                  <div>
                    <label className="flex items-center gap-2 text-gray-700 mb-3">
                      <Phone className="h-4 w-4 text-[#B42025]" />
                      <span>Mobile Number *</span>
                    </label>
                    <input
                      name="MobileNo"
                      type="number"
                      placeholder="10 Digit Mobile Number"
                      className={`w-full px-6 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#B42025] transition-colors bg-white ${
                        formik.errors.MobileNo && formik.touched.MobileNo
                          ? "border-red-500"
                          : ""
                      }`}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.MobileNo}
                    />
                  </div>

                  {/* Property ID */}
                  <div>
                    <label className="flex items-center gap-2 text-gray-700 mb-3">
                      <MapPin className="h-4 w-4 text-[#B42025]" />
                      <span>Preferred Property *</span>
                    </label>
                    <select
                      name="PropertyID"
                      className={`w-full px-6 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#B42025] transition-colors bg-white ${
                        formik.errors.PropertyID && formik.touched.PropertyID
                          ? "border-red-500"
                          : ""
                      }`}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.PropertyID}
                    >
                      <option value={0}>Select a Property</option>
                      {PropertyList?.map((prop: any) => (
                        <option key={prop?.ID} value={prop?.ID}>
                          {prop?.PropertyName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Position ID */}
                  <div>
                    <label className="flex items-center gap-2 text-gray-700 mb-3">
                      <Briefcase className="h-4 w-4 text-[#B42025]" />
                      <span>Position Applying For *</span>
                    </label>
                    <select
                      name="PositionID"
                      className={`w-full px-6 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#B42025] transition-colors bg-white ${
                        formik.errors.PositionID && formik.touched.PositionID
                          ? "border-red-500"
                          : ""
                      }`}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.PositionID}
                    >
                      <option value={0}>Select a Position</option>
                      {PositionList?.map((pos: any) => (
                        <option key={pos?.ID} value={pos?.ID}>
                          {pos?.Title}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Total Experience */}
                  <div>
                    <label className="flex items-center gap-2 text-gray-700 mb-3">
                      <Clock className="h-4 w-4 text-[#B42025]" />
                      <span>Total Experience *</span>
                    </label>
                    <input
                      name="TotalExperience"
                      type="text"
                      placeholder="e.g. 5 Years"
                      className={`w-full px-6 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#B42025] transition-colors bg-white ${
                        formik.errors.TotalExperience &&
                        formik.touched.TotalExperience
                          ? "border-red-500"
                          : ""
                      }`}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.TotalExperience}
                    />
                  </div>

                  {/* Current Company */}
                  <div>
                    <label className="flex items-center gap-2 text-gray-700 mb-3">
                      <Building2 className="h-4 w-4 text-[#B42025]" />
                      <span>Current Company</span>
                    </label>
                    <input
                      name="CurrentCompany"
                      type="text"
                      placeholder="Enter Current Company"
                      className={`w-full px-6 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#B42025] transition-colors bg-white ${
                        formik.errors.CurrentCompany &&
                        formik.touched.CurrentCompany
                          ? "border-red-500"
                          : ""
                      }`}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.CurrentCompany}
                    />
                  </div>

                  {/* Current Salary */}
                  <div>
                    <label className="flex items-center gap-2 text-gray-700 mb-3">
                      <IndianRupee className="h-4 w-4 text-[#B42025]" />
                      <span>
                        Current Salary (Annual){" "}
                        {formik?.values?.CurrentCompany && "*"}
                      </span>
                    </label>
                    <input
                      name="CurrentSalary"
                      type="number"
                      placeholder="Enter Current Salary"
                      className={`w-full px-6 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#B42025] transition-colors bg-white ${
                        formik.errors.CurrentSalary &&
                        formik.touched.CurrentSalary
                          ? "border-red-500"
                          : ""
                      }`}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.CurrentSalary}
                    />
                  </div>

                  {/* Expected Salary */}
                  <div>
                    <label className="flex items-center gap-2 text-gray-700 mb-3">
                      <IndianRupee className="h-4 w-4 text-[#B42025]" />
                      <span>Expected Salary (Annual) *</span>
                    </label>
                    <input
                      name="ExpectedSalary"
                      type="number"
                      placeholder="Enter Expected Salary"
                      className={`w-full px-6 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#B42025] transition-colors bg-white ${
                        formik.errors.ExpectedSalary &&
                        formik.touched.ExpectedSalary
                          ? "border-red-500"
                          : ""
                      }`}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.ExpectedSalary}
                    />
                  </div>

                  {/* Notice Period */}
                  <div>
                    <label className="flex items-center gap-2 text-gray-700 mb-3">
                      <Calendar className="h-4 w-4 text-[#B42025]" />
                      <span>
                        Notice Period {formik?.values?.CurrentCompany && "*"}
                      </span>
                    </label>
                    <input
                      name="NoticePeriod"
                      type="text"
                      placeholder="e.g. 30 Days"
                      className={`w-full px-6 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#B42025] transition-colors bg-white ${
                        formik.errors.NoticePeriod &&
                        formik.touched.NoticePeriod
                          ? "border-red-500"
                          : ""
                      }`}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.NoticePeriod}
                    />
                  </div>

                  {/* Resume Upload */}
                  <div>
                    <label className="flex items-center gap-2 text-gray-700 mb-3">
                      <Upload className="h-4 w-4 text-[#B42025]" />
                      <span>Upload Resume *</span>
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        className="hidden"
                        id="resume-upload"
                      />
                      <label
                        htmlFor="resume-upload"
                        className={`flex items-center justify-between w-full px-6 py-4 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-[#B42025] transition-colors bg-white ${
                          formik.errors.UploadResume &&
                          formik.touched.UploadResume
                            ? "border-red-500"
                            : ""
                        }`}
                      >
                        <span className="text-gray-500">
                          {formik.values.UploadResume
                            ? formik.values.UploadResume.name
                            : "Choose PDF/DOC File"}
                        </span>
                        <Upload className="h-5 w-5 text-[#B42025]" />
                      </label>
                    </div>
                  </div>

                  <div className="md:col-span-2 mt-8">
                    <Button
                      type="submit"
                      size="lg"
                      disabled={formLoading}
                      className="w-full bg-[#B42025] hover:bg-[#8F1A1E] text-white py-6 text-lg group"
                    >
                      <Send className="mr-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      Submit Application
                    </Button>
                    <p className="text-center text-gray-500 text-sm mt-4">
                      Our HR team will review your application and get back to
                      you soon.
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
